import * as Cmp from "@impero-com/common/utils/comparison";

import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { useOrgUserProfileCookie } from "utils/profileCookie";

import { useOrgDisplayInfo } from "../../../../utils/queries/useOrgDisplayInfoSuspenseQuery";
import {
    TASK_PREFIX,
    FIRST_REVIEWER_TASK_PREFIX,
    SECOND_REVIEWER_TASK_PREFIX,
    MAX_BULK_IMPORT_TASKS,
    MAX_BULK_IMPORT_REVIEWER_TASKS,
    TASK_FIELDS,
} from "../process/constants";

export const EXPORT_CONFIG = {
    FILENAME: "control-template",
    FILE_FORMAT: "xlsx",
    MAIN_SHEET_NAME: "Bulk Import Controls",
    INSTRUCTIONS_SHEET_NAME: "Instructions",
};

export interface TemplateColumn {
    label: string;
    example: string;
    type: "string" | "number";
}

interface TaskExample {
    title: string;
    instructions: string;
    type: string;
    options: string;
    required: string;
    condition?: {
        ref: string;
        isOrNot: string;
        value: string;
        showOrHide: string;
    };
}

const emptyExample: TaskExample = {
    title: "",
    instructions: "",
    type: "",
    options: "",
    required: "",
    condition: { ref: "", isOrNot: "", value: "", showOrHide: "" },
};

type TaskField = (typeof TASK_FIELDS)[number];

const getExampleValue = (field: TaskField, example: TaskExample) => {
    switch (field) {
        case "Title":
            return example.title;
        case "Instructions":
            return example.instructions;
        case "Type":
            return example.type;
        case "Options":
            return example.options;
        case "Required":
            return example.required;
        case "Condition_ref":
            return example.condition?.ref ?? "";
        case "Condition_is_or_not":
            return example.condition?.isOrNot ?? "";
        case "Condition_value":
            return example.condition?.value ?? "";
        case "Condition_show_or_hide":
            return example.condition?.showOrHide ?? "";
    }
};

const generateTaskColumns = (
    prefix: string,
    maxTasks: number,
    examples?: Array<TaskExample>
): Array<TemplateColumn> => {
    return Array.from({ length: maxTasks }, (_, index) => {
        const taskIndex = index + 1;
        const example = examples?.[index] ?? emptyExample;

        return TASK_FIELDS.map(
            (field) =>
                ({
                    label: `${prefix}${taskIndex}${field}`,
                    example: getExampleValue(field, example),
                    type: "string",
                }) satisfies TemplateColumn
        );
    }).flat();
};

const taskExamples = [
    {
        title: "Text Input Question",
        instructions: "Please enter your response in the text field",
        type: "TextInput",
        options: "",
        required: "N",
    },
    {
        title: "Selection Question",
        instructions: "Select one option from the radio button group",
        type: "RadioButtonGroup",
        options: "Yes;No;Not Applicable",
        required: "Y",
    },
    {
        title: "Long Answer Question",
        instructions: "Please provide a detailed response in the text area",
        type: "TextArea",
        options: "",
        required: "N",
        condition: {
            ref: "Task2",
            isOrNot: "is",
            value: "Yes",
            showOrHide: "show",
        },
    },
    {
        title: "Dropdown Selection",
        instructions: "Select an option from the dropdown menu",
        type: "Dropdown",
        options: "Option A;Option B;Option C;Option D",
        required: "N",
    },
    {
        title: "Document Upload",
        instructions: "Please upload the required documentation",
        type: "FileUpload",
        options: "",
        required: "N",
    },
];

const firstReviewerExamples = [
    {
        title: "First Review Question",
        instructions:
            "Review the submitted information and provide your assessment",
        type: "RadioButtonGroup",
        options: "Approve;Reject;Request Changes",
        required: "Y",
    },
    {
        title: "Additional First Reviewer Comments",
        instructions: "Provide any additional feedback or observations",
        type: "TextArea",
        options: "",
        required: "N",
    },
];

const secondReviewerExamples = [
    {
        title: "Second Review Question",
        instructions:
            "Review both the submission and first reviewer's assessment",
        type: "RadioButtonGroup",
        options: "Approve;Reject;Request Further Review",
        required: "Y",
    },
    {
        title: "Final Comments",
        instructions: "Add final review comments or instructions",
        type: "TextArea",
        options: "",
        required: "N",
    },
];

const getInstructionsContent = () => {
    return [
        ["BULK CONTROL IMPORTER"],
        [""],
        ["Fields not explicitly required may be left blank."],
        [""],
        ["-- Basic Fields --"],
        ["ControlProgramName", "The name of the control program (required)"],
        ["Title", "The title of the control (required)"],
        ["Description", "The description of the control"],
        [
            "CoveredPeriod",
            "One of CurrentMonth, PreviousMonth, TwoMonthsBeforeDueDate, ThreeMonthsBeforeDueDate, CurrentTwoMonthPeriod, CurrentQuarter, PreviousQuarter, CurrentHalfYear, PreviousHalfYear, CurrentYear, PreviousYear, NextYear",
        ],
        ["LoginRequired", "One of Y or N (defaults to N)"],
        ["Calendar", "Name of the calendar to use for the control (optional)"],
        [
            "NotificationSentDaysBefore",
            "How many days before the due date should the notification be sent (defaults to 5)",
        ],
        [""],
        ["-- Recurrence Settings --"],
        [
            "RecurrenceType",
            "One of Date, Weekly, Monthly, EveryTwoMonths, Quarterly, HalfYearly, Yearly (defaults to Monthly)",
        ],
        [
            "RecurrenceStartDate",
            "Date at which the recurrence should start, in YYYY-MM-DD format (defaults to today)",
        ],
        [
            "RecurrenceOffsetDays",
            "Number of days after the end of the interval to schedule the control on (required if RecurrenceType is Monthly, EveryTwoMonths, Quarterly, HalfYearly or Yearly)",
        ],
        [
            "DateRecurrenceDay",
            "The date for a recurrence of type Date, in YYYY-MM-DD format (required when RecurrenceType is Date)",
        ],
        [
            "WeeklyRecurrenceDays",
            "A comma-separated ',' list of week days, each should be one of Monday, Tuesday, Wednesday, Thursday, Friday, Saturday or Sunday (required if RecurrenceType is Weekly)",
        ],
        [
            "WeeklyRecurrenceFrequency",
            "Repeat every X week, a value between 1 and 6 (defaults to 1, only used if RecurrenceType is Weekly)",
        ],
        [
            "MonthlyRecurrenceMonths",
            "A comma-separated ',' list of months, may be one of January, February, March, April, May, June, July, August, September, October, November, December (required if RecurrenceType is Monthly)",
        ],
        [
            "EveryTwoMonthsRecurrenceTwoMonths",
            "A comma-separated ',' list of two-months, may be one of JanuaryFebruary, MarchApril, MayJune, JulyAugust, SeptemberOctober, NovemberDecember (required if RecurrenceType is EveryTwoMonths)",
        ],
        [
            "QuarterlyRecurrenceQuarters",
            "A comma-separated ',' list of quarters, may be one of First, Second, Third, Fourth (required if RecurrenceType is Quarterly)",
        ],
        [
            "HalfYearlyRecurrenceHalfYears",
            "A comma-separated ',' list of half-years, may be one of First, Second (required if RecurrenceType is HalfYearly)",
        ],
        ["---"],
        [
            "Control Tags: $categoryName",
            "$categoryName should be the name of an existing category. The value should be a semicolon-separated ';' list of values. Example: ControlTags: Country could have value Denmark;United Kingdom",
        ],
        [
            "",
            "All available categories are exported with the template. These columns can be left blank or deleted if not needed.",
        ],
        ["---"],
        ["-- Tasks --"],
        ["Task1Title", "Title of the first task"],
        ["Task1Instructions", "Instructions for the first task"],
        [
            "Task1Type",
            "One of Heading, TextInput, TextArea, NumericInput, RadioButtonGroup, CheckboxGroup, Dropdown, FileUpload (required if Task1Title is set)",
        ],
        [
            "Task1Options",
            "A semicolon-separated ';' list of options (required if Task1Type is CheckboxGroup, RadioButtonGroup, or Dropdown)",
        ],
        [
            "Task1Required",
            "Y if the task is required, N if not (defaults to N)",
        ],
        [
            "Task1Condition_ref",
            "Reference to another task in the format 'Task<number>' (e.g., 'Task1') that this task depends on",
        ],
        [
            "Task1Condition_is_or_not",
            "One of 'is' or 'not' to specify the condition logic",
        ],
        [
            "Task1Condition_value",
            "The value to compare against (must be one of the options from the referenced task)",
        ],
        [
            "Task1Condition_show_or_hide",
            "One of 'show' or 'hide' to specify visibility behavior",
        ],
        ["", "There are equivalent fields for Task2, Task3, .. Task20"],
        ["", "The maximum number of tasks is 50"],
        [
            "",
            "Note: Conditional tasks can only reference RadioButtonGroup, CheckboxGroup, or Dropdown tasks that come before them",
        ],
        ["---"],
        ["-- First Reviewer Tasks --"],
        ["FirstReviewerTask1Title", "Title of the first reviewer's first task"],
        [
            "FirstReviewerTask1Instructions",
            "Instructions for the first reviewer's first task",
        ],
        [
            "FirstReviewerTask1Type",
            "One of TextInput, TextArea, NumericInput, RadioButtonGroup, CheckboxGroup, Dropdown, FileUpload (required if FirstReviewerTask1Title is set)",
        ],
        [
            "FirstReviewerTask1Options",
            "A semicolon-separated ';' list of options (required if FirstReviewerTask1Type is CheckboxGroup, RadioButtonGroup, or Dropdown)",
        ],
        [
            "FirstReviewerTask1Required",
            "Y if the task is required, N if not (defaults to N)",
        ],
        [
            "FirstReviewerTask1Condition_ref",
            "Reference to another first reviewer task (e.g., 'FirstReviewerTask1')",
        ],
        [
            "FirstReviewerTask1Condition_is_or_not",
            "One of 'is' or 'not' to specify the condition logic",
        ],
        ["FirstReviewerTask1Condition_value", "The value to compare against"],
        [
            "FirstReviewerTask1Condition_show_or_hide",
            "One of 'show' or 'hide' to specify visibility behavior",
        ],
        [
            "",
            "There are equivalent fields for FirstReviewerTask2, FirstReviewerTask3, ..., up to 20 tasks",
        ],
        ["---"],
        ["-- Second Reviewer Tasks --"],
        [
            "SecondReviewerTask1Title",
            "Title of the second reviewer's first task",
        ],
        [
            "SecondReviewerTask1Instructions",
            "Instructions for the second reviewer's first task",
        ],
        [
            "SecondReviewerTask1Type",
            "One of TextInput, TextArea, NumericInput, RadioButtonGroup, CheckboxGroup, Dropdown, FileUpload (required if SecondReviewerTask1Title is set)",
        ],
        [
            "SecondReviewerTask1Options",
            "A semicolon-separated ';' list of options (required if SecondReviewerTask1Type is CheckboxGroup, RadioButtonGroup, or Dropdown)",
        ],
        [
            "SecondReviewerTask1Required",
            "Y if the task is required, N if not (defaults to N)",
        ],
        [
            "SecondReviewerTask1Condition_ref",
            "Reference to another second reviewer task (e.g., 'SecondReviewerTask1')",
        ],
        [
            "SecondReviewerTask1Condition_is_or_not",
            "One of 'is' or 'not' to specify the condition logic",
        ],
        ["SecondReviewerTask1Condition_value", "The value to compare against"],
        [
            "SecondReviewerTask1Condition_show_or_hide",
            "One of 'show' or 'hide' to specify visibility behavior",
        ],
        [
            "",
            "There are equivalent fields for SecondReviewerTask2, SecondReviewerTask3, ..., up to 20 tasks",
        ],
        [
            "",
            "Note: Conditions in reviewer tasks can only reference other tasks within the same reviewer group",
        ],
    ];
};

export function useControlTemplate() {
    const orgDisplayInfo = useOrgDisplayInfo();
    const orgProfileCookie = useOrgUserProfileCookie();
    const { data: tagsData } = useTagsSuspenseQuery();

    const getBaseColumns = (): Array<TemplateColumn> => [
        {
            label: "ControlProgramName",
            example: "Example Control Program",
            type: "string",
        },
        { label: "Title", example: "Example Control 01", type: "string" },
        {
            label: "Description",
            example: "Example control description",
            type: "string",
        },
        { label: "CoveredPeriod", example: "CurrentQuarter", type: "string" },
        { label: "LoginRequired", example: "N", type: "string" },
        { label: "Calendar", example: "", type: "string" },
        { label: "NotificationSentDaysBefore", example: "5", type: "number" },
        { label: "RecurrenceType", example: "Monthly", type: "string" },
        { label: "RecurrenceStartDate", example: "2024-01-31", type: "string" },
        { label: "RecurrenceOffsetDays", example: "5", type: "number" },
        { label: "DateRecurrenceDay", example: "2024-06-15", type: "string" },
        {
            label: "WeeklyRecurrenceDays",
            example: "Monday,Wednesday",
            type: "string",
        },
        { label: "WeeklyRecurrenceFrequency", example: "2", type: "number" },
        {
            label: "MonthlyRecurrenceMonths",
            example: "January,April,July,October",
            type: "string",
        },
        {
            label: "EveryTwoMonthsRecurrenceTwoMonths",
            example:
                "JanuaryFebruary,MarchApril,MayJune,JulyAugust,SeptemberOctober,NovemberDecember",
            type: "string",
        },
        {
            label: "QuarterlyRecurrenceQuarters",
            example: "First,Second,Third,Fourth",
            type: "string",
        },
        {
            label: "HalfYearlyRecurrenceHalfYears",
            example: "First,Second",
            type: "string",
        },
    ];

    const categoryMap = tagsData.tagCategories
        .filter((category) => category.active)
        .sort((a, b) => Cmp.string()(a.name, b.name))
        .reduce((map, category) => {
            const categoryTags = tagsData.tags
                .filter(
                    (tag) => tag.tagCategoryId === category.id && tag.active
                )
                .map((tag) => tag.name)
                .join(";");

            map.set(category.name, categoryTags);
            return map;
        }, new Map<string, string>());

    const getTagColumns = (): Array<TemplateColumn> =>
        Array.from(categoryMap.entries()).map(([categoryName, tags]) => ({
            label: "ControlTags: " + categoryName,
            example: tags,
            type: "string",
        }));

    const getExportMetadata = () => ({
        date: new Date(),
        organization: {
            name: orgDisplayInfo.name,
        },
        user: {
            firstName: orgProfileCookie?.firstName ?? "",
            lastName: orgProfileCookie?.lastName ?? "",
            email: orgProfileCookie?.email ?? "",
        },
    });

    const getTaskColumns = (): Array<TemplateColumn> => {
        return generateTaskColumns(
            TASK_PREFIX,
            MAX_BULK_IMPORT_TASKS,
            taskExamples
        );
    };

    const getFirstReviewerTaskColumns = (): Array<TemplateColumn> => {
        return generateTaskColumns(
            FIRST_REVIEWER_TASK_PREFIX,
            MAX_BULK_IMPORT_REVIEWER_TASKS,
            firstReviewerExamples
        );
    };

    const getSecondReviewerTaskColumns = (): Array<TemplateColumn> => {
        return generateTaskColumns(
            SECOND_REVIEWER_TASK_PREFIX,
            MAX_BULK_IMPORT_REVIEWER_TASKS,
            secondReviewerExamples
        );
    };

    return {
        getBaseColumns,
        getTagColumns,
        getTaskColumns,
        getFirstReviewerTaskColumns,
        getSecondReviewerTaskColumns,
        getExportMetadata,
        getInstructionsContent,
    };
}
